import type { ReactNode } from 'react';
import { Icon } from '../components/Icon';
import { STEP_SCREENS, type Screen } from './state';

const BACKDROP = 'linear-gradient(135deg, #629677 0%, #BFDCF9 100%)';

export function Logo() {
  return <img src="/brand/peoplexyz-logo-black.svg" alt="PeopleXYZ" width={120} height={42} style={{ display: 'block', width: 120, height: 'auto' }} />;
}

export function FundingLogos() {
  return (
    <img
      src="/brand/funded-by-uk-government-techfirst-black.png"
      alt="Funded by UK Government. TechFirst."
      width={280}
      height={26}
      decoding="async"
      style={{ display: 'block', width: '100%', maxWidth: 280, height: 'auto' }}
    />
  );
}

export function Headline({ as: Tag = 'h1', className, style }: { as?: 'h1' | 'p'; className?: string; style?: React.CSSProperties }) {
  return (
    <Tag className={`disp ${className ?? ''}`} style={style}>
      Hire new tech talent with a government-<em className="em">funded</em> hiring package worth over £7,000.
    </Tag>
  );
}

const PROGRESS = ['Role', 'About you', 'Quick check', 'Your result', 'Secure your place'];
const PROGRESS_INDEX: Partial<Record<Screen, number>> = { role: 0, you: 1, check: 2, checking: 3, result: 3, secure: 4, booking: 4, done: 5 };

export function Shell({ screen, wide, onBack, children }: { screen: Screen; wide: boolean; onBack: (() => void) | null; children: ReactNode }) {
  const stepIndex = STEP_SCREENS.indexOf(screen);
  const isStep = stepIndex >= 0;
  const labels = ['Role', 'About you', 'Quick check'];
  const pill = isStep
    ? `Step ${stepIndex + 1} of 3`
    : ({ checking: 'Checking', result: 'Your result', secure: 'Secure your place', booking: 'Book your call' } as Partial<Record<Screen, string>>)[screen];
  const wideInner = screen === 'result' || screen === 'secure' || screen === 'booking';

  return (
    <div className="page">
      <a className="skip" href="#main">
        Skip to content
      </a>
      <div className={`root ${wide ? 'wide' : 'narrow'}`} lang="en-GB">
        {wide ? (
          <aside className="aside" style={{ background: BACKDROP }} aria-label="PeopleXYZ funded hiring places">
            <Logo />
            {screen === 'landing' ? (
              <div style={{ margin: 'clamp(40px,10vh,96px) 0 auto', maxWidth: 480 }}>
                <div className="eyebrow">Funded hiring places</div>
                <Headline style={{ fontSize: 'clamp(28px,2.5vw,38px)', lineHeight: 1.18, letterSpacing: '-.02em', marginTop: 16, textWrap: 'pretty' }} />
                <p style={{ fontSize: 16, lineHeight: 1.55, marginTop: 18 }}>No upfront cost. A flat £799 fee, only if you hire.</p>
              </div>
            ) : (
              <div style={{ margin: 'clamp(40px,10vh,96px) 0 auto' }}>
                <p className="disp" style={{ fontSize: 26, lineHeight: 1.2, letterSpacing: '-.01em' }}>
                  Check your eligibility
                </p>
                <p style={{ fontSize: 15, lineHeight: 1.5, marginTop: 8 }}>3 quick steps, then your programme and package value.</p>
                <ol style={{ marginTop: 32 }} aria-label="Your progress">
                  {PROGRESS.map((name, i) => {
                    const at = PROGRESS_INDEX[screen] ?? 0;
                    const done = i < at;
                    const current = i === at;
                    return (
                      <li
                        key={name}
                        aria-current={current ? 'step' : undefined}
                        style={{ display: 'grid', gridTemplateColumns: '28px minmax(0,1fr)', gap: 14 }}
                      >
                        <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <span
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: done ? '#000' : current ? '#fff' : 'transparent',
                              border: `1.5px solid ${done || current ? '#000' : 'rgba(0,0,0,.3)'}`,
                              color: '#fff',
                              transition: 'all 200ms cubic-bezier(.2,0,0,1)',
                            }}
                          >
                            <Icon name="check" size={14} style={{ opacity: done ? 1 : 0 }} />
                          </span>
                          {i < PROGRESS.length - 1 ? <span style={{ width: 1.5, height: 22, background: 'rgba(0,0,0,.25)' }} /> : null}
                        </div>
                        <span style={{ fontSize: 15, lineHeight: '28px', fontWeight: current ? 700 : 400 }}>
                          {name}
                          {done ? <span className="sr-only"> (completed)</span> : null}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
            <div>
              <FundingLogos />
            </div>
          </aside>
        ) : (
          <header className="head" style={{ background: BACKDROP }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 44 }}>
              <Logo />
              {pill ? <span className="pill">{pill}</span> : null}
            </div>
            {isStep ? (
              <div
                role="progressbar"
                aria-label="Your progress"
                aria-valuemin={1}
                aria-valuemax={3}
                aria-valuenow={stepIndex + 1}
                aria-valuetext={`Step ${stepIndex + 1} of 3, ${labels[stepIndex]}`}
                style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 6, marginTop: 14 }}
              >
                {[0, 1, 2].map((p) => (
                  <div
                    key={p}
                    style={{
                      height: 4,
                      borderRadius: 999,
                      background: p <= stepIndex ? '#000' : 'rgba(0,0,0,.15)',
                      transition: 'background 320ms cubic-bezier(.2,0,0,1)',
                    }}
                  />
                ))}
              </div>
            ) : null}
            {screen === 'landing' ? (
              <div style={{ padding: '20px 0 4px' }}>
                <div className="eyebrow">Funded hiring places</div>
                <Headline className="hl" />
                <p style={{ fontSize: 15, lineHeight: 1.5, marginTop: 10 }}>No upfront cost. A flat £799 fee, only if you hire.</p>
              </div>
            ) : null}
          </header>
        )}
        <main className="main" id="main">
          <div className={`inner${wideInner ? ' w6' : ''}`}>
            {onBack ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 44, margin: '-6px 0 8px -10px' }}>
                <button type="button" className="back" onClick={onBack}>
                  <Icon name="arrow-left" size={16} /> Back
                </button>
                {wide && isStep ? (
                  <span className="s13 mute">
                    Step {stepIndex + 1} of 3 · {labels[stepIndex]}
                  </span>
                ) : null}
              </div>
            ) : null}
            <div className="body">{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
}
